import {
  Box,
  List,
  Skeleton,
  useTheme,
  Collapse,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
} from '@mui/material';

import { NoteListItem } from './NoteListItem';
import { useNoteListItems } from './useNoteListItems';
import { Icon } from '../../../../../wrapper-components/Icon/Icon';
import type { Note } from '../../../types/response';

export interface NoteListItemsProps {
  notes: Note[];
  isAnnotationWorkspaceLoading: boolean;
}

export const NoteListItems = ({ notes, isAnnotationWorkspaceLoading }: NoteListItemsProps) => {
  const theme = useTheme();

  const { expandedGroups, groupedNotes, handleToggleGroup, groupBy } = useNoteListItems({ notes });

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
      {isAnnotationWorkspaceLoading ? (
        <Box sx={{ display: 'flex', height: '100%', p: theme.spacing(2) }}>
          <Skeleton variant="rectangular" height={80} width="100%" />
        </Box>
      ) : groupBy === 'none' ? (
        <List sx={{ p: 0 }}>
          {notes.map((note) => (
            <NoteListItem key={note.noteId} note={note} />
          ))}
        </List>
      ) : groupedNotes ? (
        <List sx={{ p: 0 }}>
          {Object.entries(groupedNotes).map(([groupKey, groupNotes]) => {
            const isExpanded = expandedGroups.has(groupKey);
            return (
              <Box key={groupKey}>
                <ListItemButton
                  onClick={() => handleToggleGroup(groupKey)}
                  sx={{
                    backgroundColor: theme.palette.grey[100],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1,
                    px: 2,
                    '&:hover': {
                      backgroundColor: theme.palette.grey[200],
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600}>
                        {groupKey} ({groupNotes.length})
                      </Typography>
                    }
                  />
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <Icon
                      id={`group-${groupKey}-expand-icon`}
                      icon={isExpanded ? 'collapse' : 'expand'}
                      width={20}
                      height={20}
                    />
                  </IconButton>
                </ListItemButton>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List sx={{ p: 0 }}>
                    {groupNotes.map((note) => (
                      <NoteListItem key={note.noteId} note={note} />
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      ) : null}
    </Box>
  );
};
